import { useMemo, useState } from 'react';
import ModalComponent from '@components/Modal/ModalComponent';
import Breadcrumb from '@components/Shared/Breadcrumb';
import { Card, CardBody } from '@components/card/card';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import AddDailyRates from './AddDailyRates';
import useGetGlDailyRatesList from '@hooks/Finance/DailyRates/useGetGlDailyRatesList';
import EditDailyRates from './EditDailyRates';
import Loading from '@components/Shared/Loader/Loading';

 

const Index: React.FC = () => {
  const [ID, setID] = useState<string>('');
  const [filter, setFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');

  const { t } = useTranslation();
  const { DailyRatesList, loading, error } = useGetGlDailyRatesList();

  // استخراج العملات الفريدة ديناميكياً من البيانات
  const uniqueCurrencies = useMemo(() => {
    if (!DailyRatesList) return [];

    const currencies = new Set<string>();

    DailyRatesList.forEach(item => {
      if (item.fromCurrencyCode) {
        currencies.add(item.fromCurrencyCode);
      }
      if (item.toCurrencyCode) {
        currencies.add(item.toCurrencyCode);
      }
    });

    // تحويل Set إلى Array وترتيبها
    return Array.from(currencies).sort();
  }, [DailyRatesList]);

  // دالة لتنسيق التاريخ
  const formatDate = (dateString: string | null): string => {
    try {
      if (!dateString) return 'غير محدد';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (error) {
      return dateString || 'تاريخ غير صالح';
    }
  };

  // دالة للتحقق من إذا كان السعر نشطاً
  const isRateActive = (startDate: string, endDate: string | null): boolean => {
    const now = new Date();
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : null;
    return now >= start && (!end || now <= end);
  };

  // تحضير بيانات الجدول
  const tableData = useMemo((): any[] => {
    return (
      DailyRatesList?.map(item => {
        const active = isRateActive(item.conversionStartDate, item.conversionEndDate);

        return {
          id: item.id,
          fromCurrencyCode: item.fromCurrencyCode,
          toCurrencyCode: item.toCurrencyCode,
          rate: item.conversionRate,
          conversionStartDate: item.conversionStartDate,
          conversionEndDate: item.conversionEndDate,
          formattedStartDate: formatDate(item.conversionStartDate),
          formattedEndDate: formatDate(item.conversionEndDate),
          isActive: active,
          rowClass: active ? 'table-success' : 'table-light'
        };
      }) || []
    );
  }, [DailyRatesList]);

  // فلترة البيانات
  const filteredData = useMemo((): any[] => {
    let data = tableData;

    // تطبيق الفلتر حسب العملة
    if (filter !== 'all') {
      data = data.filter(item => item.fromCurrencyCode === filter || item.toCurrencyCode === filter);
    }
    // if (filter !== 'all') {
    //   data = data.filter(item => item.fromCurrencyCode === filter  );
    // }

    // تطبيق البحث
    if (searchTerm) {
      data = data.filter(
        item =>
          item.fromCurrencyCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.toCurrencyCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.rate.toString().includes(searchTerm) ||
          item.formattedStartDate.includes(searchTerm) ||
          item.formattedEndDate.includes(searchTerm)
      );
    }

    return data;
  }, [tableData, filter, searchTerm]);

  console.log(filteredData);
  

  // دالة للحصول على اسم العملة
  const getCurrencyName = (code: string): string => {
    const currencyNames: { [key: string]: string } = {
      USD: 'الدولار',
      SAR: 'الريال السعودي',
      SR: 'الريال السعودي',
      EGP: 'الجنيه المصري',
      EGY: 'الجنيه المصري',
      EUR: 'يورو',
      YER: 'الريال يمني',
      GBP: 'جنيه إسترليني'
    };

    return currencyNames[code] || code;
  };

  return (
    <div>
      <div className='d-flex align-items-center  '>
        <Breadcrumb
          BreadcrumbList={[
            {
              name: 'Home',
              link: ':moduleHome'
            },
            {
              name: 'DailyRates',
              link: null
            }
          ]}
          PageName='DailyRates'
        />

        <div className='ms-auto'>
          <Link to='' className='btn me-1 btn-theme' data-bs-toggle='modal' data-bs-target='#modalAddDailyRates'>
            <i className='fa fa-plus-circle fa-fw me-1'></i>
            {t('New')}
          </Link>
        </div>
      </div>

      <Card>
        <CardBody>
          {/* فلترات سريعة - ديناميكية */}
          <div className='row mb-3'>
            <div className='col-md-8'>
              <div className='d-flex flex-wrap gap-2 align-items-center'>
                <span className='fw-bold'>تصفية حسب:</span>
                <button
                  type='button'
                  className={`btn ${filter === 'all' ? 'btn-theme' : 'btn-outline-secondary'}`}
                  onClick={() => setFilter('all')}
                >
                  جميع العملات
                </button>

                {uniqueCurrencies.map(currency => (
                  <button
                    key={currency}
                    type='button'
                    className={`btn ${filter === currency ? 'btn-theme' : 'btn-outline-secondary'}`}
                    onClick={() => setFilter(currency)}
                    // title={`${currencyStats[currency]?.total || 0} سعر - ${currencyStats[currency]?.active || 0} نشط`}
                  >
                    {getCurrencyName(currency)}
                  </button>
                ))}
              </div>
            </div>
            <div className='col-md-4'>
              <div className='input-group'>
                <span className='input-group-text'>
                  <i className='bi bi-search'></i>
                </span>
                <input
                  type='text'
                  className='form-control'
                  placeholder='ابحث في أسعار الصرف...'
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button className='btn btn-outline-secondary' type='button' onClick={() => setSearchTerm('')}>
                    <i className='bi bi-x'></i>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* معلومات النتائج */}
          {filteredData.length > 0 && (
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <div>
                <span className='text-muted'>
                  عرض <strong>{filteredData.length}</strong> سعر صرف
                  {filter !== 'all' && (
                    <span>
                      {' '}
                      لـ <strong>{getCurrencyName(filter)}</strong>
                    </span>
                  )}
                  {searchTerm && (
                    <span>
                      {' '}
                      في نتائج البحث عن "<strong>{searchTerm}</strong>"
                    </span>
                  )}
                </span>
              </div>
              <div>
                <span className='badge bg-success'>{filteredData.filter(item => item.isActive).length} سعر نشط</span>
              </div>
            </div>
          )}

          <Loading loading={loading} error={error}>
            {filteredData.length > 0 ? (
              <div className='table-responsive'>
                <table className='table table-hover align-middle'>
                  <thead className='bg-light'>
                    <tr>
                      <th>العملة</th>
                      <th>سعر الصرف</th>
                      <th>من تاريخ</th>
                      <th>إلى تاريخ</th>
                      <th>الحالة</th>
                      <th className='text-end'>الإجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map(item => (
                      // <tr key={item.id} className={item.rowClass}>
                      <tr key={item.id}>
                        <td>
                          <div className='d-flex align-items-center   gap-2'>
                            <div className='text-center'>
                              <div className='badge bg-primary'>{item.fromCurrencyCode}</div>
                              <div className='small text-muted' style={{ fontSize: '0.7rem' }}>
                                من
                              </div>
                            </div>
                            <div className='text-muted'>/</div>
                            <div className='text-center'>
                              <div className='badge bg-success'>{item.toCurrencyCode}</div>
                              <div className='small text-muted' style={{ fontSize: '0.7rem' }}>
                                إلى
                              </div>
                            </div>
                          </div>
                        </td>

                        <td>
                          <strong className='text-success'>{item.rate.toFixed(4)}</strong>
                        </td>
                        <td>
                          <div className='text-nowrap'>{item.formattedStartDate}</div>
                        </td>
                        <td>
                          <div className='text-nowrap'>{item.formattedEndDate}</div>
                        </td>
                        <td>
                          <span className={`badge ${item.isActive ? 'bg-success' : 'bg-secondary'}`}>
                            {item.isActive ? 'نشط' : 'منتهي'}
                          </span>
                        </td>
                        <td className='text-end'>
                          <button
                            type='button'
                            className='btn btn-sm btn-outline-theme'
                            data-bs-toggle='modal'
                            data-bs-target='#modalEditDailyRates'
                            onClick={() => setID(item.id)}
                          >
                            <i className='far fa-lg fa-fw fa-edit me-1'></i>
                            تعديل
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className='text-center py-5'>
                <i className='bi bi-inbox fa-4x text-muted mb-3'></i>
                <h5 className='text-muted'>لا توجد بيانات</h5>
                <p className='text-muted'>لم يتم العثور على أسعار صرف تطابق معايير البحث</p>
                {(filter !== 'all' || searchTerm) && (
                  <button
                    className='btn btn-theme mt-2'
                    onClick={() => {
                      setFilter('all');
                      setSearchTerm('');
                    }}
                  >
                    عرض جميع الأسعار
                  </button>
                )}
              </div>
            )}
          </Loading>
        </CardBody>
      </Card>

      <ModalComponent title={t('إضافة معدل يومي')} id='modalAddDailyRates' size='lg'>
        <AddDailyRates />
      </ModalComponent>

      <ModalComponent title={t('EditDailyRate')} id='modalEditDailyRates' size='lg'>
        {ID && <EditDailyRates ID={ID} />}
      </ModalComponent>
    </div>
  );
};

export default Index;
